import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import useLocalStorage from '../hooks/useLocalStorage';
import getTrialStatus from '../utils/trialStatus';

const AuthContext = React.createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useLocalStorage('token', '');
    const [refreshToken, setRefreshToken] = useLocalStorage('refreshToken', '');
    const config = { headers: { 'Content-Type': 'application/json' } };
    const [now, setNow] = useState(() => Date.now());
    const { isTrialActive, remainingDays, hasAccess, isSubscribed, freeAccess, hasClassAccess } =
        getTrialStatus(currentUser, now);

    const refreshUser = useCallback(async () => {
        if (!token) return;
        const response = await axios.post('/api/user/verifyToken', {}, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setCurrentUser(response.data);
        return response.data;
    }, [token]);

    useEffect(() => {
        const updateAccess = () => {
            setNow(Date.now());
            refreshUser().catch(() => {});
        };
        const intervalId = window.setInterval(updateAccess, 60_000);
        window.addEventListener('focus', updateAccess);
        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener('focus', updateAccess);
        };
    }, [refreshUser]);

    useEffect(() => {
        setLoading(true);
        const verifyUser = async () => {
            try {
                if (token) {
                    const response = await axios.post(
                        '/api/user/verifyToken',
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (response) {
                        setCurrentUser(response.data);
                    }
                } else {
                    setCurrentUser(null);
                }
            } catch (err) {
                console.log(err);
                setToken('');
                setRefreshToken('');
                setCurrentUser(null);
            }
            setLoading(false);
        };
        verifyUser();
    }, [token]);

    async function signUp(username, email, password1, password2, role, grade) {
        try {
            const response = await axios.post(
                '/api/user/register',
                { username, email, password1, password2, role, grade },
                config
            );
            if (response.data.token) {
                setCurrentUser(response.data.user);
                setToken(response.data.token);
                setRefreshToken(response.data.refresh);
            }
            return response.data;
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    async function login({ username, password }) {
        try {
            const identifier = username.trim();
            const response = await axios.post(
                '/api/user/login',
                // The Django API uses `username`; the legacy Express API uses
                // `email`. Send both so the same login form works with either.
                { username: identifier, email: identifier, password },
                config
            );
            if (response) {
                setCurrentUser(response.data.user || null);
                setToken(response.data.token);
                setRefreshToken(response.data.refresh || '');
            }
            return response;
        } catch (err) {
            throw err;
        }
    }

    async function logout() {
        setLoading(true);
        try {
            if (token && refreshToken) {
                await axios.post(
                    '/api/user/logout',
                    { refresh: refreshToken },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
        } catch (err) {
            console.log(err);
        }
        setCurrentUser(null);
        setToken('');
        setRefreshToken('');
        setLoading(false);
    }

    const value = { 
        currentUser, 
        token, 
        loading, 
        hasAccess, 
        isSubscribed,
        freeAccess,
        hasClassAccess,
        refreshUser,
        isTrialActive, 
        remainingDays,
        signUp, 
        login, 
        logout 
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
